/**
 * The ONE precision contract for a quantity of goods.
 *
 * Every quantity column in the ERP cluster is `NUMERIC(18,6)` in the product's
 * BASE unit (`docs/erp/modern-architecture-principles.md` § P3), so the ceiling
 * an input or a display may spell is six fraction digits — and it lives here,
 * not at ~40 call sites, so a `decimalPlaces={2}` / `Math.round` / `parseInt`
 * cannot creep back in and silently truncate a 24.076543 MT line.
 *
 * "Whole units" is a fact about the product's base UNIT, never a product
 * boolean: `uomDecimalPlaces[token]` is 0 for a counted unit (each, dozen,
 * carton…) and 6 for a measured one (kg, l, m…) and for the permissive legacy
 * `unit`. Pass the product's `base_uom` to `quantityDecimalPlaces` and the
 * input refuses a fraction on a product that cannot have one.
 */
import { uomDecimalPlaces, type Uom } from '../../generated/config-enums/uom';

/** Fraction digits of a `NUMERIC(18,6)` quantity column. */
export const QUANTITY_DECIMAL_PLACES = 6;

/**
 * The fraction digits a quantity of a product may carry: the base unit's own
 * `decimal_places` from the UoM catalog, or the column ceiling when the base
 * unit is not known at this surface.
 */
export function quantityDecimalPlaces(baseUom: Uom | null | undefined): number {
  return baseUom ? uomDecimalPlaces[baseUom] : QUANTITY_DECIMAL_PLACES;
}

export interface FormatQuantityOptions {
  /** Ceiling on fraction digits. Defaults to the column's six. */
  decimalPlaces?: number;
}

/**
 * Render a quantity through the viewer's number formatter WITHOUT losing
 * precision: `formatNumber` from `useUserPreferences` is `Intl.NumberFormat`,
 * whose default `maximumFractionDigits` is 3 — so a bare `formatNumber(qty)`
 * shows 24.077 for a stored 24.076543 and nothing in the pipeline can see it.
 * Trailing zeros are suppressed (`minimumFractionDigits: 0`) so a plain 12
 * still reads "12".
 */
export function formatQuantity(
  value: number,
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string,
  options?: FormatQuantityOptions,
): string {
  return formatNumber(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: options?.decimalPlaces ?? QUANTITY_DECIMAL_PLACES,
  });
}

/**
 * Whether `value` carries more fraction digits than `decimalPlaces` allows —
 * the client-side twin of the backend's `exceeds_precision`, which REFUSES an
 * over-precise entry rather than rounding it.
 *
 * Compared with a tolerance far below the sixth decimal so binary-float noise
 * on a legitimately entered value (0.1 + 0.2) is not mistaken for precision.
 */
export function isOverPrecision(value: number, decimalPlaces: number): boolean {
  if (!Number.isFinite(value)) return false;
  const atScale = Number(value.toFixed(decimalPlaces));
  return Math.abs(value - atScale) > 1e-9;
}

/**
 * The base-unit PROJECTION of a package count — `count × factor` at the
 * column's six decimals, the same arithmetic the server runs
 * (`round(quantity_in_packaging × packaging_quantity_in_base, 6)`).
 *
 * For DISPLAY and optimistic overlays only: the server recomputes the base
 * quantity from the count and the snapshotted factor on every write and never
 * trusts a client-computed one, so this value is never what a caller submits as
 * authoritative. Quantizing at six places keeps binary-float noise
 * (`3 × 0.1 = 0.30000000000000004`) out of what the user reads.
 */
export function projectedBaseQuantity(quantityInPackaging: number, factor: number): number {
  return Number((quantityInPackaging * factor).toFixed(QUANTITY_DECIMAL_PLACES));
}
